import { create } from 'zustand';
import type { MediaItem } from '../models/types';
import { AudioService } from '../services/audioService';
import { ApiService, buildShowEndpoint } from '../services/api';
import { SongModel } from '../models/Song';

const FILLER_KEYWORDS = ['tuning', 'tunning', 'crowd', 'banter', 'announcements'];

function isFillerSong(song: MediaItem): boolean {
  const title = decodeURIComponent(song.title || '').toLowerCase();
  const hasFillerKeyword = FILLER_KEYWORDS.some(keyword =>
    new RegExp(`\\b${keyword}\\b`).test(title)
  );
  const isShort = (song.duration ?? 0) <= 180;
  return hasFillerKeyword && isShort;
}

interface PlayerState {
  audioService: AudioService;
  queue: MediaItem[];
  currentIndex: number;
  isPlaying: boolean;
  isFetchingSong: boolean;
  currentPosition: number;
  duration: number;

  // Share loading state
  isLoadingFromShare: boolean;
  playbackBlocked: boolean;
  blockedSongInfo: { title: string; artist: string } | null;

  // Computed
  currentItem: MediaItem | null;
  queueLength: number;

  // Actions
  initialize: () => void;
  updateQueue: (queue: MediaItem[]) => void;
  addToQueue: (items: MediaItem[]) => void;
  addToEndOfQueue: (items: MediaItem[]) => Promise<void>;
  addToQueueAfterCurrentItem: (items: MediaItem[], shouldRemoveOldTracks?: boolean) => Promise<void>;
  playItemAt: (index: number) => Promise<void>;
  removeItemAt: (index: number) => Promise<void>;
  play: () => Promise<void>;
  pause: () => Promise<void>;
  stop: () => Promise<void>;
  skipToNext: () => Promise<void>;
skipToPrevious: () => Promise<void>;
  seekTo: (position: number) => Promise<void>;
  shuffleUpcoming: () => Promise<void>;
  fetchRandomSong: (collection: string, type?: string, customPayload?: string, source?: string) => Promise<MediaItem | null>;
  fetchRandomShow: (collection: string, type?: string, customPayload?: string, source?: string) => Promise<MediaItem[] | null>;
  setQueueAndBeginPlaying: (queue?: MediaItem[]) => Promise<void>;
  fetchMoreSongs: (count: number) => Promise<void>;
  clearUpcomingSongs: () => Promise<void>;
  isSongPartOfJam: (song: MediaItem) => boolean;
  fetchJamSequence: (collection: string, showId: string, currentTrack: string, source?: string) => Promise<MediaItem[] | null>;
  fetchFullShow: (collection: string, showId: string, source?: string) => Promise<MediaItem[] | null>;
  addFullShowAfterCurrentSong: (collection: string, showId: string, currentTrack: string, source?: string) => Promise<boolean>;
  loadFullShowFromBeginning: (collection: string, showId: string, source?: string) => Promise<boolean>;
  loadSharedSong: (params: { collection: string; songId: string; showId: string; mode: string; source: string }) => Promise<boolean>;
  setLoadingFromShare: (loading: boolean) => void;
  clearPlaybackBlocked: () => void;
}

export const usePlayerStore = create<PlayerState>((set, get) => {
  const audioService = new AudioService();
  let autoFetchInterval: ReturnType<typeof setInterval> | null = null;
  let isAutoFetching = false;

  // Auto-fetch subscription - runs regardless of UI
  const startAutoFetch = () => {
    if (autoFetchInterval) return;

    autoFetchInterval = setInterval(async () => {
      const { currentIndex, queue, isFetchingSong } = get();
      if (isFetchingSong || isAutoFetching) return;

      // Check if we need to fetch more songs
      const songsRemaining = queue.length - currentIndex - 1;
      if (songsRemaining <= 3 && currentIndex >= 0) {
        isAutoFetching = true;
        try {
          await get().fetchMoreSongs(5);
        } finally {
          isAutoFetching = false;
        }
      }
    }, 2000); // Check every 2 seconds
  };

  const stopAutoFetch = () => {
    if (autoFetchInterval) {
      clearInterval(autoFetchInterval);
      autoFetchInterval = null;
    }
  };

  // Set up audio service event listeners
  audioService.on('ended', () => {
    const { currentIndex, queue } = get();
    if (currentIndex < queue.length - 1) {
      get().skipToNext();
    }
  });

  // Listen for gapless track changes
  audioService.on('trackchanged', (newIndex: number) => {
    set({ currentIndex: newIndex });
  });

  audioService.on('play', () => {
    set({ isPlaying: true });
    startAutoFetch();
  });

  audioService.on('pause', () => {
    set({ isPlaying: false });
    stopAutoFetch();
  });

  // Throttle timeupdate to ~1x/sec to avoid excessive re-renders
  let lastTimeUpdate = 0;
  audioService.on('timeupdate', (currentTime: number, duration: number) => {
    const now = Date.now();
    if (now - lastTimeUpdate >= 1000 || currentTime === 0) {
      lastTimeUpdate = now;
      set({ currentPosition: currentTime, duration: duration || 0 });
    }
  });

  // Auto-skip on audio error
  let consecutiveErrors = 0;
  const MAX_CONSECUTIVE_ERRORS = 3;
  audioService.on('error', (error: Error) => {
    const { currentIndex, queue, currentItem } = get();

    // Check if this is a browser autoplay blocked error
    const isNotAllowedError = error.name === 'NotAllowedError' ||
      error.message?.includes('NotAllowedError') ||
      error.message?.includes("play() failed because the user didn't interact");

    if (isNotAllowedError) {
      console.error('Playback blocked by browser:', error.message);
      // Don't skip - just mark as blocked and let user interact
      if (currentItem) {
        set({
          playbackBlocked: true,
          blockedSongInfo: { title: currentItem.title, artist: currentItem.artist || '' }
        });
      }
      return;
    }

    consecutiveErrors++;
    console.error(`Audio error (${consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS}):`, error);
    if (currentItem) {
      console.error('Failed to load audio for:', {
        title: currentItem.title,
        artist: currentItem.artist,
        url: currentItem.id,
      });
    }

    // Stop trying after too many consecutive errors
    if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
      console.error('Too many consecutive audio errors, stopping playback');
      get().pause();
      consecutiveErrors = 0;
      return;
    }

    if (currentIndex < queue.length - 1) {
      get().skipToNext();
    } else {
      console.error('No more tracks in queue');
      consecutiveErrors = 0;
    }
  });

  // Reset error counter on successful play
  audioService.on('play', () => {
    consecutiveErrors = 0;
    set({ isPlaying: true, isLoadingFromShare: false });
    startAutoFetch();
  });

  return {
    audioService,
    queue: [],
    currentIndex: -1,
    isPlaying: false,
    isFetchingSong: false,
    currentPosition: 0,
    duration: 0,
    isLoadingFromShare: false,
    playbackBlocked: false,
    blockedSongInfo: null,

    get currentItem(): MediaItem | null {
      const { currentIndex, queue } = get();
      return currentIndex >= 0 && currentIndex < queue.length ? queue[currentIndex] : null;
    },

    get queueLength(): number {
      return get().queue.length;
    },

    initialize: () => {
      // Any initialization logic
    },

    setLoadingFromShare: (loading: boolean) => {
      set({ isLoadingFromShare: loading });
    },

    clearPlaybackBlocked: () => {
      set({ playbackBlocked: false, blockedSongInfo: null, isLoadingFromShare: false });
    },

    updateQueue: (queue: MediaItem[]) => {
      audioService.updateQueue(queue);
      set({ queue: [...queue] });
    },

    addToQueue: (items: MediaItem[]) => {
      const { queue } = get();
      const newQueue = [...queue, ...items];
      audioService.updateQueue(newQueue);
      set({ queue: newQueue });
    },

    addToEndOfQueue: async (items: MediaItem[]) => {
      if (items.length === 0) return;
      await audioService.addQueueItems(items);
      const { queue } = get();
      set({ queue: [...queue, ...items] });
    },

    addToQueueAfterCurrentItem: async (items: MediaItem[], shouldRemoveOldTracks = false) => {
      if (items.length === 0) return;

      const { currentIndex, queue } = get();
      const insertIndex = Math.max(0, currentIndex + 1);

      // Insert items after current position
      for (let i = 0; i < items.length; i++) {
        await audioService.insertQueueItem(insertIndex + i, items[i]);
      }

      if (shouldRemoveOldTracks) {
        // Remove tracks after the inserted items
        const removeStart = insertIndex + items.length;
        await audioService.removeRange(removeStart, queue.length + items.length);
        const newQueue = [...queue.slice(0, insertIndex), ...items];
        set({ queue: newQueue });
      } else {
        const newQueue = [...queue];
        newQueue.splice(insertIndex, 0, ...items);
        set({ queue: newQueue });
      }
    },

    playItemAt: async (index: number) => {
      set({ currentIndex: index });
      await audioService.skipToQueueItem(index);
    },

    removeItemAt: async (index: number) => {
      await audioService.removeQueueItemAt(index);
      const { queue, currentIndex } = get();
      const newQueue = [...queue];
      newQueue.splice(index, 1);

      let newCurrentIndex = currentIndex;
      if (index < currentIndex) {
        newCurrentIndex--;
      } else if (index === currentIndex && newQueue.length > 0) {
        // If we removed the current item, stay at the same index or move to next
        if (newCurrentIndex >= newQueue.length) {
          newCurrentIndex = newQueue.length - 1;
        }
      }

      set({ queue: newQueue, currentIndex: newCurrentIndex });
    },

    play: async () => {
      // Clear blocked state when user manually plays
      const { playbackBlocked } = get();
      if (playbackBlocked) {
        set({ playbackBlocked: false, blockedSongInfo: null, isLoadingFromShare: false });
      }
      await audioService.play();
    },

    pause: async () => {
      await audioService.pause();
    },

    stop: async () => {
      await audioService.stop();
      set({ isPlaying: false });
    },

    skipToNext: async () => {
      const { currentIndex, queue } = get();
      if (currentIndex < queue.length - 1) {
        const newIndex = currentIndex + 1;
        set({ currentIndex: newIndex });
        await audioService.skipToQueueItem(newIndex);
      }
    },

    skipToPrevious: async () => {
      const { currentIndex } = get();
      if (currentIndex > 0) {
        const newIndex = currentIndex - 1;
        set({ currentIndex: newIndex });
        await audioService.skipToQueueItem(newIndex);
      }
    },

    seekTo: async (position: number) => {
      set({ currentPosition: position });
      await audioService.seekTo(position);
    },

    shuffleUpcoming: async () => {
      const { currentIndex, queue } = get();
      if (currentIndex >= queue.length - 1) return; // No upcoming songs to shuffle

      const upcomingSongs = queue.slice(currentIndex + 1);
      
      // Shuffle the upcoming songs
      const shuffledUpcoming = [...upcomingSongs].sort(() => Math.random() - 0.5);
      
      // Reconstruct the queue
      const newQueue = [
        ...queue.slice(0, currentIndex + 1),
        ...shuffledUpcoming
      ];

      // Update the queue in the audio service
      await audioService.updateQueue(newQueue);
      set({ queue: newQueue });
    },

    fetchRandomSong: async (collection: string, type?: string, customPayload?: string, source?: string) => {
      try {
        set({ isFetchingSong: true });
        const songData = await ApiService.fetchSong(collection, type, customPayload, source);
        if (songData) {
          const mediaItem = SongModel.transformSongToMediaItem(songData, source);
          return mediaItem;
        }
        return null;
      } catch (error) {
        console.error('Error fetching random song:', error);
        return null;
      } finally {
        set({ isFetchingSong: false });
      }
    },

    fetchRandomShow: async (collection: string, type?: string, customPayload?: string, source?: string) => {
      try {
        set({ isFetchingSong: true });
        const showData = await ApiService.fetchShow(collection, type, customPayload, source);
        if (showData) {
          // Sort by track number
          const sortedSongs = showData.sort((a, b) => {
            const aTrack = parseInt(a.track) || 0;
            const bTrack = parseInt(b.track) || 0;
            return aTrack - bTrack;
          });

          const mediaItems = sortedSongs.map(song => SongModel.transformSongToMediaItem(song, source));
          return mediaItems;
        }
        return null;
      } catch (error) {
        console.error('Error fetching random show:', error);
        return null;
      } finally {
        set({ isFetchingSong: false });
      }
    },

    setQueueAndBeginPlaying: async (queue?: MediaItem[]) => {
      const itemsToPlay = queue || get().queue;
      if (itemsToPlay.length === 0) return;

      set({ queue: [...itemsToPlay], currentIndex: 0 });
      await audioService.updateQueue(itemsToPlay);
      await audioService.skipToQueueItem(0);
    },

    isSongPartOfJam: (song: MediaItem) => {
      return song.title?.trim().endsWith('>') || false;
    },

    fetchJamSequence: async (collection: string, showId: string, currentTrack: string, source?: string) => {
      try {
        set({ isFetchingSong: true });
        const endpoint = buildShowEndpoint(collection, 'showId', showId, source);
        const response = await fetch(endpoint);
        if (!response.ok) throw new Error('Failed to fetch show');

        const jsonData = await response.json();
        const songs = jsonData.song_list;

        // Sort by track number
        songs.sort((a: any, b: any) => {
          const aTrack = parseInt(a.track) || 0;
          const bTrack = parseInt(b.track) || 0;
          return aTrack - bTrack;
        });

        const mediaItems = songs.map((song: any) => ({ ...SongModel.transformSongToMediaItem(SongModel.fromJson(song), source), artUri: '/live_shuffle_jams.png' }));

        // Filter to only include songs after the current track
        const currentTrackNum = parseInt(currentTrack);
        const remainingSongs = mediaItems.filter((item: MediaItem) => {
          const trackStr = item.extras?.track as string;
          const trackNum = parseInt(trackStr);
          return trackNum > currentTrackNum;
        });

        // Keep only jam songs (ending with >)
        const jamSongs: MediaItem[] = [];
        for (const song of remainingSongs) {
          if (song.title?.trim().endsWith('>')) {
            jamSongs.push(song);
          } else {
            jamSongs.push(song);
            break; // Stop at first non-jam song
          }
        }

        return jamSongs;
      } catch (error) {
        console.error('Error fetching jam sequence:', error);
        return null;
      } finally {
        set({ isFetchingSong: false });
      }
    },

    fetchFullShow: async (collection: string, showId: string, source?: string) => {
      try {
        set({ isFetchingSong: true });
        const endpoint = buildShowEndpoint(collection, 'showId', showId, source);
        const response = await fetch(endpoint);
        if (!response.ok) throw new Error('Failed to fetch show');

        const jsonData = await response.json();
        const songs = jsonData.song_list;

        // Sort by track number
        songs.sort((a: any, b: any) => {
          const aTrack = parseInt(a.track) || 0;
          const bTrack = parseInt(b.track) || 0;
          return aTrack - bTrack;
        });

        const mediaItems = songs.map((song: any) => ({ ...SongModel.transformSongToMediaItem(SongModel.fromJson(song), source), artUri: '/live_shuffle_fullshows.png' }));
        return mediaItems;
      } catch (error) {
        console.error('Error fetching full show:', error);
        return null;
      } finally {
        set({ isFetchingSong: false });
      }
    },

    addFullShowAfterCurrentSong: async (collection: string, showId: string, currentTrack: string, source?: string) => {
      try {
        const fullShow = await get().fetchFullShow(collection, showId, source);
        if (!fullShow) return false;

        const currentTrackNum = parseInt(currentTrack);
        const remainingSongs = fullShow.filter(item => {
          const trackStr = item.extras?.track as string;
          const trackNum = parseInt(trackStr);
          return trackNum > currentTrackNum;
        });

        if (remainingSongs.length > 0) {
          await get().addToQueueAfterCurrentItem(remainingSongs, false);
          return true;
        }
        return false;
      } catch (error) {
        console.error('Error adding full show after current song:', error);
        return false;
      }
    },

    loadFullShowFromBeginning: async (collection: string, showId: string, source?: string) => {
      try {
        const fullShow = await get().fetchFullShow(collection, showId, source);
        if (!fullShow) return false;

        const { currentIndex } = get();
        await get().addToQueueAfterCurrentItem(fullShow, false);
        await get().playItemAt(currentIndex + 1);
        return true;
      } catch (error) {
        console.error('Error loading full show from beginning:', error);
        return false;
      }
    },

    fetchMoreSongs: async (count: number) => {
      try {
        // Import here to avoid circular dependency
        const { useBandStore } = await import('./bandStore');
        const { useSettingsStore } = await import('./settingsStore');

        const bandStore = useBandStore.getState();
        const { fullShowMode, continueJamsMode, skipFillerSongs } = useSettingsStore.getState();

        // Get active bands with their customizations
        const activeBands = bandStore.getActiveBands();

        if (activeBands.length === 0) return;

        set({ isFetchingSong: true });
        const freshSongs: MediaItem[] = [];

        if (fullShowMode) {
          // Fetch one full show
          const randomBand = bandStore.getRandomValue(activeBands);
          const showSongs = await get().fetchRandomShow(randomBand.collection, 'random', 'random', randomBand.source);
          if (showSongs) {
            freshSongs.push(...showSongs.map(s => ({ ...s, artUri: '/live_shuffle_fullshows.png' })));
          }
        } else {
          // Fetch individual songs
          const promises = Array.from({ length: count }, async () => {
            const randomBand = bandStore.getRandomValue(activeBands);

            // Apply band customizations (similar to Flutter)
            let customValue = 'random';
            let type = 'random';
            if (randomBand.selectedYears && randomBand.selectedYears.length > 0 ||
                randomBand.selectedYearMonths && randomBand.selectedYearMonths.length > 0) {
              const allOptions = [
                ...(randomBand.selectedYears || []),
                ...(randomBand.selectedYearMonths || []),
              ];
              customValue = bandStore.getRandomValue(allOptions);
              if (customValue.split('-').length > 1) {
                type = 'yearmonth';
              } else {
                type = 'year';
              }
            }

            // Retry loop to skip filler songs when setting is enabled
            const maxFillerRetries = 4;
            for (let attempt = 0; attempt < maxFillerRetries; attempt++) {
              const song = await get().fetchRandomSong(randomBand.collection, type, customValue, randomBand.source);
              if (song && skipFillerSongs && isFillerSong(song)) {
                continue;
              }
              return song;
            }
            return null;
          });

          const songs = await Promise.all(promises);
          for (const song of songs) {
            if (song) {
              freshSongs.push({ ...song, artUri: '/live_shuffle_discover.png' });

              // Continue Jams: if enabled and song ends with ">", fetch the jam sequence
              if (continueJamsMode && get().isSongPartOfJam(song)) {
                const showId = song.extras?.show_id || song.extras?.showId;
                const track = song.extras?.track;
                const collection = song.extras?.collection || song.artist || '';
                const source = song.extras?.source || 'ls';
                if (showId && track) {
                  const jamSongs = await get().fetchJamSequence(collection, showId, track, source);
                  if (jamSongs) {
                    freshSongs.push(...jamSongs.map(s => ({ ...s, artUri: '/live_shuffle_jams.png' })));
                  }
                }
              }
            }
          }
        }

        if (freshSongs.length > 0) {
          await get().addToEndOfQueue(freshSongs);
        }
      } catch (error) {
        console.error('Error fetching more songs:', error);
      } finally {
        set({ isFetchingSong: false });
      }
    },

    clearUpcomingSongs: async () => {
      try {
        const { queue, currentIndex } = get();

        // Keep only songs up to and including current song
        const newQueue = queue.slice(0, currentIndex + 1);

        // Update the queue
        await get().updateQueue(newQueue);

        // Fetch new songs to fill the queue
        await get().fetchMoreSongs(5);
      } catch (error) {
        console.error('Error clearing upcoming songs:', error);
      }
    },

    loadSharedSong: async ({ collection, songId, showId, mode, source }) => {
      try {
        set({ isFetchingSong: true, isLoadingFromShare: true });

        // Activate the shared band so auto-fetch works after shared content ends
        const { useBandStore } = await import('./bandStore');
        const bandStore = useBandStore.getState();
        bandStore.setActiveBandFromShare(collection, source);

        if (mode === 'normal') {
          // Fetch just the single song
          const songData = await ApiService.fetchSong(collection, 'songId', songId, source);
          if (!songData) return false;
          const mediaItem = { ...SongModel.transformSongToMediaItem(songData, source), artUri: '/live_shuffle_discover.png' };
          await get().setQueueAndBeginPlaying([mediaItem]);
          return true;
        }

        if (mode === 'fullShow') {
          // Fetch the full show, start at the shared song's position
          const showSongs = await ApiService.fetchShow(collection, 'showId', showId, source);
          if (!showSongs) return false;

          const sortedSongs = showSongs.sort((a, b) => {
            const aTrack = parseInt(a.track) || 0;
            const bTrack = parseInt(b.track) || 0;
            return aTrack - bTrack;
          });
          const mediaItems = sortedSongs.map(song => ({ ...SongModel.transformSongToMediaItem(song, source), artUri: '/live_shuffle_fullshows.png' }));

          // Find the shared song's position
          const sharedIndex = mediaItems.findIndex(item => item.extras?.songId === songId);
          const startIndex = sharedIndex >= 0 ? sharedIndex : 0;

          set({ queue: mediaItems, currentIndex: startIndex });
          await audioService.updateQueue(mediaItems);
          await audioService.skipToQueueItem(startIndex);
          return true;
        }

        if (mode === 'continueJams') {
          // Fetch the full show, extract jam sequence starting at the shared song
          const showSongs = await ApiService.fetchShow(collection, 'showId', showId, source);
          if (!showSongs) return false;

          const sortedSongs = showSongs.sort((a, b) => {
            const aTrack = parseInt(a.track) || 0;
            const bTrack = parseInt(b.track) || 0;
            return aTrack - bTrack;
          });
          const mediaItems = sortedSongs.map(song => ({ ...SongModel.transformSongToMediaItem(song, source), artUri: '/live_shuffle_jams.png' }));

          // Find the shared song
          const sharedIndex = mediaItems.findIndex(item => item.extras?.songId === songId);
          if (sharedIndex < 0) return false;

          // Build jam sequence: shared song + subsequent songs ending with > + first non-jam song
          const jamSequence: MediaItem[] = [mediaItems[sharedIndex]];
          for (let i = sharedIndex + 1; i < mediaItems.length; i++) {
            const prevTitle = mediaItems[i - 1].title?.trim() || '';
            if (prevTitle.endsWith('>')) {
              jamSequence.push(mediaItems[i]);
              // If this song doesn't end with >, it's the last one in the sequence
              if (!mediaItems[i].title?.trim().endsWith('>')) break;
            } else {
              break;
            }
          }

          await get().setQueueAndBeginPlaying(jamSequence);
          return true;
        }

        return false;
      } catch (error: any) {
        const isNotAllowed = error?.name === 'NotAllowedError' ||
          error?.message?.includes('NotAllowedError') ||
          error?.message?.includes("play() failed because the user didn't interact");

        if (isNotAllowed) {
          // Queue is loaded, just need user interaction to start playback
          const { currentItem } = get();
          set({
            playbackBlocked: true,
            blockedSongInfo: currentItem
              ? { title: currentItem.title, artist: currentItem.artist || '' }
              : null,
          });
          return true;
        }

        console.error('Error loading shared song:', error);
        return false;
      } finally {
        set({ isFetchingSong: false });
      }
    },
  };
});